import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';
import Spinner from '@/components/shared/Spinner';
import { cn } from '@/lib/utils';
import { type ButtonVariantProps, buttonVariants } from './button-variants';

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		ButtonVariantProps {
	asChild?: boolean;
	loading?: boolean;
	loadingText?: string;
	icon?: React.ReactNode;
	iconRight?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			variant,
			size,
			asChild = false,
			loading = false,
			loadingText,
			icon,
			iconRight,
			children,
			disabled,
			...props
		},
		ref
	) => {
		const Comp = asChild ? Slot : 'button';

		// Determine spinner size based on button size
		const getSpinnerSize = (): 'xs' | 'sm' | 'md' | 'lg' | 'xl' | string => {
			if (size === 'sm') return 'sm';
			if (size === 'xl') return 'w-14 h-14'; // Custom larger size for xl buttons
			if (size === 'lg') return 'lg';
			return 'sm'; // default size
		};

		// Determine spinner variant based on button variant
		const getSpinnerVariant = (): 'current' | 'white' => {
			// Use white spinner for dark button variants
			if (variant === 'default' || variant === 'destructive' || variant === 'success') {
				return 'white';
			}
			// Use current color for other variants
			return 'current';
		};

		const isDisabled = disabled || loading;

		// When asChild is true, Slot expects exactly one child element
		// So we should only pass through the children without wrapping
		if (asChild) {
			return (
				<Comp
					className={cn('cursor-pointer', buttonVariants({ variant, size }), className)}
					ref={ref}
					disabled={isDisabled}
					{...props}
				>
					{children}
				</Comp>
			);
		}

		return (
			<Comp
				className={cn(buttonVariants({ variant, size }), className, loading && 'relative')}
				ref={ref}
				disabled={isDisabled}
				{...props}
			>
				{/* Always render content to maintain button width - render as separate children for proper gap spacing */}
				{icon && (
					<span className={cn('inline-flex items-center', loading && 'invisible')}>{icon}</span>
				)}
				{/* No wrapper around `children` when not loading — a `hidden sm:inline`
				    label would otherwise sit inside a real flex item that still consumes
				    a `gap` slot next to the icon even while fully hidden. `display: contents`
				    would fix that too, but it also strips the element's box, which breaks
				    Cypress's `.contains(...)` visibility/click checks on the label text. */}
				{children && (loading ? <span className="invisible">{children}</span> : children)}
				{iconRight && (
					<span className={cn('inline-flex items-center', loading && 'invisible')}>
						{iconRight}
					</span>
				)}
				{/* Spinner overlay when loading */}
				{loading && (
					<span
						className="absolute inset-0 flex items-center justify-center gap-2"
						data-testid="button-spinner"
					>
						<Spinner
							size={getSpinnerSize()}
							variant={getSpinnerVariant()}
							visible={loading}
							aria-label={loadingText || 'Loading'}
						/>
						{loadingText && <span>{loadingText}</span>}
					</span>
				)}
			</Comp>
		);
	}
);
Button.displayName = 'Button';

export { Button };
